/**
 * LeanCustoms / Dexwork order costs.
 *
 * Dexwork OrderCreate does not include costs — each line is POSTed to
 * /api/orders/{order_id}/order_costs after the order exists (see leancustoms-orders edge function).
 * API cost_type values and Dexwork UI labels (NL):
 * | cost_type           | Dexwork kostenregel (NL)                     | Our amount source                          |
 * |---------------------|----------------------------------------------|--------------------------------------------|
 * | transport_air       | Luchtvrachtkosten                            | Tariff or price/kg × chargeable weight     |
 * | transport_extra_eu  | Vrachtkosten van laadplaat tot aankomst EU   | Pickup + export clearance; FCL also + origin THC (EC included in THC) |
 * | transport_intra_eu  | Vrachtkosten binnen EU                       | Main shipping estimate (sea / some terms)  |
 * | insurance           | Verzekeringskosten                           | 0 (required line; filled in Dexwork)       |
 * | other               | Overige kosten                               | —                                          |
 *
 * Currency is always EUR.
 *
 * Air import (e.g. FCA): both transport_air and transport_extra_eu are required.
 * - Luchtvrachtkosten ← main air freight estimate
 * - Vrachtkosten van laadplaat tot aankomst EU ← pickup + export clearance
 */

export type OrderCostType =
  | "transport_intra_eu"
  | "transport_extra_eu"
  | "transport_air"
  | "insurance"
  | "other";

/** Dexwork UI label (Dutch) for each cost_type — used in previews and docs. */
export const LEANCUSTOMS_ORDER_COST_LABELS_NL: Record<OrderCostType, string> = {
  transport_air: "Luchtvrachtkosten",
  transport_extra_eu: "Laadplaats",
  transport_intra_eu: "Vrachtkosten binnen EU",
  insurance: "Verzekeringskosten",
  other: "Overige kosten",
};

export const LEANCUSTOMS_ORDER_COST_CURRENCY = "EUR" as const;

export type OrderCostAttribute = {
  cost_type: OrderCostType;
  amount: number;
  currency: typeof LEANCUSTOMS_ORDER_COST_CURRENCY;
  allocation_type: AllocationType;
};

type TransportTypeInfo = { abbreviation?: string; name?: string } | TransportTypeInfo[];

/**
 * Map booking transport to Dexwork transport_mode: maritime | air | road | rail | inland | other.
 * Uses booking.transport_type id when present (1 = air, 2 = maritime) so air costs are not missed
 * when transport_type_info abbreviation is absent on list-shaped bookings.
 */
export function mapBookingTransportToLeanCustoms(booking: {
  transport_type?: number | null;
  transport_type_info?: TransportTypeInfo;
}): string {
  if (booking.transport_type === 1) return "air";
  if (booking.transport_type === 2) return "maritime";

  const info = booking.transport_type_info;
  const row = Array.isArray(info) ? info[0] : info;
  const abbr = row?.abbreviation ?? "";
  const name = row?.name ?? "";
  const s = `${abbr}${name}`.toUpperCase();
  if (s.includes("AIR")) return "air";
  if (s.includes("SEA") || s.includes("MARITIME")) return "maritime";
  if (s.includes("ROAD") || s.includes("TRUCK")) return "road";
  if (s.includes("RAIL")) return "rail";
  if (s.includes("INLAND")) return "inland";
  return "maritime";
}

/**
 * Returns the list of required cost_type values for order_costs_attributes.
 * When incoterm is not specified or not recognised, returns [].
 *
 * Rules:
 * - Import: EXW,FCA,FAS,FOB → transport_extra_eu + insurance; CPT,CFR,DPU,DAP,DDP → transport_intra_eu + insurance;
 *   CIF,CIP → transport_intra_eu only. Maritime CFR/CIF: transport_intra_eu NOT required.
 * - Export: EXW,FCA → transport_intra_eu + insurance; FAS,FOB → insurance only;
 *   CPT,CFR,CIF,CIP,DPU,DAP,DDP → transport_extra_eu.
 * - Air: add transport_air to all of the above.
 */
export function getRequiredOrderCostTypes(
  incotermCode: string | null | undefined,
  orderType: "import" | "export",
  transportMode: string
): OrderCostType[] {
  const code = (incotermCode || "").toUpperCase().trim();
  if (!code) return [];

  const isAir = (transportMode || "").toLowerCase() === "air";
  const isMaritime = (transportMode || "").toLowerCase() === "maritime";

  const add: OrderCostType[] = [];

  if (orderType === "import") {
    if (["EXW", "FCA", "FAS", "FOB"].includes(code)) {
      add.push("transport_extra_eu", "insurance");
    } else if (["CPT", "CFR", "DPU", "DAP", "DDP"].includes(code)) {
      add.push("insurance");
      if (!(isMaritime && code === "CFR")) {
        add.push("transport_intra_eu");
      }
    } else if (["CIF", "CIP"].includes(code)) {
      if (!(isMaritime && code === "CIF")) {
        add.push("transport_intra_eu");
      }
    }
  } else {
    if (["EXW", "FCA"].includes(code)) {
      add.push("transport_intra_eu", "insurance");
    } else if (["FAS", "FOB"].includes(code)) {
      add.push("insurance");
    } else if (["CPT", "CFR", "CIF", "CIP", "DPU", "DAP", "DDP"].includes(code)) {
      add.push("transport_extra_eu");
    }
  }

  if (isAir) {
    add.push("transport_air");
  }

  return [...new Set(add)];
}

export type AllocationType = "gross_weight" | "invoice_amount" | "net_weight" | null;

export function getAllocationTypeForCostType(costType: OrderCostType): AllocationType {
  switch (costType) {
    case "transport_extra_eu":
    case "transport_intra_eu":
    case "transport_air":
      return "gross_weight";
    case "insurance":
      return "invoice_amount";
    case "other":
    default:
      return null;
  }
}

/**
 * Which cost_type should receive the estimated main shipping amount (air/sea leg).
 */
export function getPrimaryTransportCostTypeForEstimate(
  incotermCode: string | null | undefined,
  orderType: "import" | "export",
  transportMode: string
): OrderCostType | null {
  const code = (incotermCode || "").toLowerCase().trim();
  const isAir = (transportMode || "").toLowerCase() === "air";
  const isMaritime = (transportMode || "").toLowerCase() === "maritime";

  if (isAir) return "transport_air";

  if (orderType === "import") {
    if (["exw", "fca", "fas", "fob"].includes(code)) return "transport_extra_eu";
    if (["cpt", "cfr", "dpu", "dap", "ddp"].includes(code)) {
      if (isMaritime && code === "cfr") return null;
      return "transport_intra_eu";
    }
    if (["cif", "cip"].includes(code)) {
      if (isMaritime && code === "cif") return null;
      return "transport_intra_eu";
    }
  } else {
    if (["exw", "fca"].includes(code)) return "transport_intra_eu";
    if (["fas", "fob"].includes(code)) return null;
    if (["cpt", "cfr", "cif", "cip", "dpu", "dap", "ddp"].includes(code)) return "transport_extra_eu";
  }

  return null;
}

/** Build order_costs_attributes payload for Dexwork order create. */
export function buildOrderCostsAttributes(args: {
  incotermCode: string;
  orderType: "import" | "export";
  transportMode: string;
  /** Pickup + export clearance → transport_extra_eu (laadplaat tot aankomst EU). */
  freightCostsToEu: number;
  /** Main transport estimate (air/sea leg) — no silent fallback; 0 when not entered. */
  estimatedShipping: number;
}): OrderCostAttribute[] {
  const required = getRequiredOrderCostTypes(args.incotermCode, args.orderType, args.transportMode);
  if (required.length === 0) return [];

  const primary = getPrimaryTransportCostTypeForEstimate(
    args.incotermCode,
    args.orderType,
    args.transportMode
  );
  const shippingForOrder = Math.max(args.estimatedShipping, 0);

  return required.map((costType) => {
    let amount = 0;
    if (costType === "transport_extra_eu") {
      amount = args.freightCostsToEu;
      if (primary === "transport_extra_eu") amount += shippingForOrder;
    } else if (costType === "transport_intra_eu") {
      amount = costType === primary ? shippingForOrder : 0;
    } else if (costType === "transport_air") {
      amount = costType === primary ? shippingForOrder : 0;
    } else if (costType === "insurance") {
      amount = 0;
    } else if (costType === primary) {
      amount = shippingForOrder;
    }
    return {
      cost_type: costType,
      amount: Number(amount),
      currency: LEANCUSTOMS_ORDER_COST_CURRENCY,
      allocation_type: getAllocationTypeForCostType(costType),
    };
  });
}

export type TransportCostDetailsInput = {
  transportMode: string;
  isFCL: boolean;
  grossWeight: number;
  totalVolume: number;
  volumeWeight: number;
  chargeableWeight: number;
  orderCosts: OrderCostAttribute[];
  primaryCostType: OrderCostType | null;
  shippingSource: "tariff" | "manual" | "override" | "none";
  tariffRate?: number | null;
  tariffUnit?: "per_kg" | "per_cbm" | "per_container" | null;
  manualRate?: number | null;
  manualUnit?: "per_kg" | "per_cbm" | "per_container" | null;
  pickupFee?: number | null;
  exportClearance?: boolean | null;
  freightCostsOverride?: number | null | undefined;
  /** FCL origin THC gross (includes export clearance when set). */
  originTerminalHandling?: number | null;
  exportClearanceIncludedInThc?: boolean;
};

function formatMainShippingBasis(input: TransportCostDetailsInput): string {
  const isAir = input.transportMode.toLowerCase() === "air";
  if (input.shippingSource === "override") return "manual override";

  if (isAir) {
    const basis = `chargeable ${input.chargeableWeight.toFixed(2)} kg (gross ${input.grossWeight} kg, volume ${input.volumeWeight.toFixed(2)} kg from ${input.totalVolume} CBM)`;
    if (input.shippingSource === "tariff" && input.tariffRate != null) {
      return `${basis} × €${input.tariffRate.toFixed(2)}/kg (tariff)`;
    }
    if (input.shippingSource === "manual" && input.manualRate != null) {
      return `${basis} × €${input.manualRate.toFixed(2)}/kg`;
    }
    return basis;
  }

  if (input.isFCL) {
    if (input.shippingSource === "tariff" && input.tariffRate != null) {
      return `1 container × €${input.tariffRate.toFixed(2)} (tariff)`;
    }
    if (input.shippingSource === "manual" && input.manualRate != null) {
      return `1 container × €${input.manualRate.toFixed(2)}`;
    }
    return "1 container";
  }

  const cbmBasis = `${input.totalVolume} CBM (gross ${input.grossWeight} kg)`;
  if (input.shippingSource === "tariff" && input.tariffRate != null) {
    return `${cbmBasis} × €${input.tariffRate.toFixed(2)}/CBM (tariff)`;
  }
  if (input.shippingSource === "manual" && input.manualRate != null) {
    return `${cbmBasis} × €${input.manualRate.toFixed(2)}/CBM`;
  }
  return cbmBasis;
}

function formatExtraEuBasis(input: TransportCostDetailsInput, includesMainShipping: boolean): string {
  if (input.freightCostsOverride !== undefined && input.freightCostsOverride !== null) {
    return `override €${input.freightCostsOverride.toFixed(2)}`;
  }
  const pickup = input.pickupFee ?? 0;
  const originThc = input.originTerminalHandling ?? 0;
  let basis: string;
  if (input.isFCL && originThc > 0) {
    const net = Math.max(0, originThc - 45);
    basis = `pick-up €${pickup} + origin THC €${originThc.toFixed(2)} (incl. export clearance €45 → net THC €${net.toFixed(2)})`;
  } else {
    const ec =
      input.exportClearance === true ? "yes (€45)" : input.exportClearance === false ? "no" : "—";
    basis = `pick-up €${pickup} + export clearance ${ec}`;
  }
  if (includesMainShipping) {
    basis += ` + main freight (${formatMainShippingBasis(input)})`;
  }
  return basis;
}

/** Lines appended to Lean Customs order details — calculation basis for each Kosten line. */
export function buildTransportCostDetailsLines(input: TransportCostDetailsInput): string[] {
  if (input.orderCosts.length === 0) return [];

  const lines: string[] = ["---", "Transport costs (EUR):"];
  const mainBasis = formatMainShippingBasis(input);
  const isAir = input.transportMode.toLowerCase() === "air";

  if (isAir) {
    lines.push(
      `Weight basis: gross ${input.grossWeight} kg | volume ${input.volumeWeight.toFixed(2)} kg (${input.totalVolume} CBM × 167) | chargeable ${input.chargeableWeight.toFixed(2)} kg`
    );
  } else if (!input.isFCL) {
    lines.push(`Volume basis: ${input.totalVolume} CBM | gross ${input.grossWeight} kg`);
  }

  for (const cost of input.orderCosts) {
    const label = LEANCUSTOMS_ORDER_COST_LABELS_NL[cost.cost_type];
    const amount = `€${cost.amount.toFixed(2)}`;
    let basis = "";

    if (
      cost.cost_type === "transport_air" ||
      cost.cost_type === "transport_intra_eu"
    ) {
      if (cost.cost_type === input.primaryCostType) {
        basis = mainBasis;
      }
    } else if (cost.cost_type === "transport_extra_eu") {
      basis = formatExtraEuBasis(input, input.primaryCostType === "transport_extra_eu");
    } else if (cost.cost_type === "insurance") {
      basis = "to be confirmed in Dexwork";
    }

    lines.push(basis ? `${label}: ${amount} — ${basis}` : `${label}: ${amount}`);
  }

  return lines;
}

/** True when this order needs a main transport estimate on a primary cost line (air/sea). */
export function orderRequiresMainShippingEstimate(
  incotermCode: string | null | undefined,
  orderType: "import" | "export",
  transportMode: string
): boolean {
  return getPrimaryTransportCostTypeForEstimate(incotermCode, orderType, transportMode) != null;
}

/** Tariff, manual rate, or explicit override > 0 — required before create when shipping estimate is needed. */
export function isMainShippingCostProvided(args: {
  requiresShippingEstimate: boolean;
  hasTariff: boolean;
  manualShipping: number;
  estimatedShippingOverride: number | undefined | null;
  estimatedTransportLoading: boolean;
}): boolean {
  if (!args.requiresShippingEstimate) return true;
  if (args.estimatedTransportLoading) return false;
  if (args.hasTariff) return true;
  if (args.manualShipping > 0) return true;
  const override = args.estimatedShippingOverride;
  return override !== undefined && override !== null && override > 0;
}
